"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, chakra } from "@chakra-ui/react";
import { NavArrowLeft, NavArrowRight } from "iconoir-react";
import React, { useMemo, useRef } from "react";
import { useButton, useCalendar, useLocale } from "react-aria";
import { Select } from "../select";
import { CalendarGrid } from "./CalendarGrid";
const NavigationButton = chakra("button");
export const Calendar = React.memo(function Calendar({ datePickerState, calendarState, colorPalette, }) {
    const { calendarProps, prevButtonProps, nextButtonProps } = useCalendar({}, calendarState);
    const { locale } = useLocale();
    const previousRef = useRef(null);
    const nextRef = useRef(null);
    const { buttonProps: previousProps } = useButton(prevButtonProps, previousRef);
    const { buttonProps: nextProps } = useButton(nextButtonProps, nextRef);
    const currentDate = calendarState.visibleRange.start;
    // Create month options
    const monthOptions = useMemo(() => {
        const months = [];
        for (let i = 0; i < 12; i++) {
            const date = new Date(2024, i, 1); // Use 2024 as base year for month names
            const monthName = new Intl.DateTimeFormat(locale, {
                month: "long",
            }).format(date);
            months.push({ label: monthName, value: i.toString() });
        }
        return months;
    }, [locale]);
    // Create year options based on minValue and maxValue constraints
    const yearOptions = useMemo(() => {
        // Get min and max years from calendar state constraints
        const minYear = calendarState.minValue?.year ??
            Math.min(new Date().getFullYear() - 100, currentDate.year);
        const maxYear = calendarState.maxValue?.year ??
            Math.max(new Date().getFullYear() + 100, currentDate.year);
        const years = [];
        for (let year = minYear; year <= maxYear; year++) {
            years.push({ label: year.toString(), value: year.toString() });
        }
        return years;
    }, [calendarState.minValue, calendarState.maxValue, currentDate.year]);
    // Get current month and year from calendar state
    const currentMonth = currentDate.month - 1; // Convert to 0-based index
    const currentYear = currentDate.year;
    // Handle month selection
    const handleMonthChange = (details) => {
        if (details.value.length > 0) {
            const newMonth = parseInt(details.value[0]) + 1; // Convert back to 1-based
            const newDate = currentDate.set({ month: newMonth });
            calendarState.setFocusedDate(newDate);
        }
    };
    // Handle year selection
    const handleYearChange = (details) => {
        if (details.value.length > 0) {
            const newYear = parseInt(details.value[0]);
            const newDate = currentDate.set({ year: newYear });
            calendarState.setFocusedDate(newDate);
        }
    };
    return (_jsxs(Box, { bg: "bg", border: "1px solid", borderColor: "border", rounded: "xl", shadow: "lg", p: 4, zIndex: "popover", maxW: "sm", ...calendarProps, children: [_jsxs(Box, { display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4, gap: 2, children: [_jsx(NavigationButton, { type: "button", ref: previousRef, ...previousProps, rounded: "md", p: 1, bg: "transparent", border: "none", cursor: prevButtonProps.isDisabled ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", _hover: !prevButtonProps.isDisabled ? { bg: "bg.subtle" } : {}, title: previousProps["aria-label"], opacity: prevButtonProps.isDisabled ? 0.4 : 1, children: _jsx(NavArrowLeft, { width: 14, height: 14 }) }), _jsx(Select, { options: monthOptions, value: [currentMonth.toString()], onValueChange: handleMonthChange, placeholder: "Month", size: "sm", portalled: false }), _jsx(Select, { options: yearOptions, value: [currentYear.toString()], onValueChange: handleYearChange, placeholder: "Year", size: "sm", portalled: false }), _jsx(NavigationButton, { type: "button", ref: nextRef, ...nextProps, rounded: "md", p: 1, bg: "transparent", border: "none", cursor: nextButtonProps.isDisabled ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", _hover: !nextButtonProps.isDisabled ? { bg: "bg.subtle" } : {}, title: nextProps["aria-label"], opacity: nextButtonProps.isDisabled ? 0.4 : 1, children: _jsx(NavArrowRight, { width: 14, height: 14 }) })] }), _jsx(CalendarGrid, { datePickerState: datePickerState, calendarState: calendarState, colorPalette: colorPalette })] }));
});
